// 스케줄 관리 유틸리티 함수

/**
 * 시간 문자열을 분으로 변환 (HH:MM -> 분)
 * @param {string} timeString - 'HH:MM' 형식의 시간 문자열
 * @returns {number} - 분 단위 시간
 */
export function parseTimeToMinutes(timeString) {
  if (!timeString || typeof timeString !== 'string') return 0;
  
  const [hours, minutes] = timeString.split(':').map(Number);
  return hours * 60 + minutes;
}

/**
 * 두 시간대가 겹치는지 확인
 * @param {object} time1 - {start: 'HH:MM', end: 'HH:MM'}
 * @param {object} time2 - {start: 'HH:MM', end: 'HH:MM'}
 * @returns {boolean} - 겹치면 true
 */
export function checkTimeConflict(time1, time2) {
  if (!time1 || !time2 || !time1.start || !time1.end || !time2.start || !time2.end) {
    return false;
  }

  const t1Start = parseTimeToMinutes(time1.start);
  const t1End = parseTimeToMinutes(time1.end);
  const t2Start = parseTimeToMinutes(time2.start);
  const t2End = parseTimeToMinutes(time2.end);

  // 시간대 겹침 확인: (start1 < end2) && (end1 > start2)
  return (t1Start < t2End && t1End > t2Start);
}

/**
 * 날짜 문자열에서 요일 이름 추출
 * @param {string} dateString - 'YYYY-MM-DD' 형식의 날짜 문자열
 * @returns {string} - 요일 이름 (monday, tuesday, ...)
 */
export function getDayOfWeek(dateString) {
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const date = new Date(dateString);
  return days[date.getDay()];
}

/**
 * 요일 이름을 한글로 변환
 * @param {string} dayName - 영문 요일 이름
 * @returns {string} - 한글 요일
 */
export function getDayNameKorean(dayName) {
  const dayMap = {
    monday: '월',
    tuesday: '화',
    wednesday: '수',
    thursday: '목',
    friday: '금',
    saturday: '토',
    sunday: '일'
  };
  return dayMap[dayName] || '';
}

/**
 * 학업시간표와 근무스케줄 충돌 체크
 * @param {object} academicSchedule - 학업시간표 객체
 * @param {object} workSchedule - 근무스케줄 객체 {workDate, startTime, endTime}
 * @returns {object} - {hasConflict: boolean, conflictingClasses: array}
 */
export function checkScheduleConflict(academicSchedule, workSchedule) {
  if (!academicSchedule || !workSchedule) {
    return { hasConflict: false, conflictingClasses: [] };
  }

  // 활성화되지 않은 학업시간표는 체크 안함
  if (!academicSchedule.isActive) {
    return { hasConflict: false, conflictingClasses: [] };
  }

  // 근무일이 학기 범위 내인지 확인
  const workDate = new Date(workSchedule.workDate);
  const startDate = new Date(academicSchedule.startDate);
  const endDate = new Date(academicSchedule.endDate);

  if (workDate < startDate || workDate > endDate) {
    // 학기 기간 외의 근무는 충돌 없음
    return { hasConflict: false, conflictingClasses: [] };
  }

  // 근무일의 요일에 해당하는 학업시간표 가져오기
  const dayName = getDayOfWeek(workSchedule.workDate);
  const daySchedule = academicSchedule.weeklySchedule?.[dayName] || [];

  if (!Array.isArray(daySchedule) || daySchedule.length === 0) {
    // 해당 요일에 수업이 없음
    return { hasConflict: false, conflictingClasses: [] };
  }

  // 각 수업과 근무시간 충돌 체크
  const conflictingClasses = [];
  const workTime = {
    start: workSchedule.startTime,
    end: workSchedule.endTime
  };

  daySchedule.forEach(classItem => {
    const classTime = {
      start: classItem.start,
      end: classItem.end
    };

    if (checkTimeConflict(workTime, classTime)) {
      conflictingClasses.push({
        subject: classItem.subject,
        time: `${classItem.start} - ${classItem.end}`
      });
    }
  });

  return {
    hasConflict: conflictingClasses.length > 0,
    conflictingClasses
  };
}

/**
 * 근무시간 계산 (분 단위)
 * @param {string} startTime - 시작시간 'HH:MM'
 * @param {string} endTime - 종료시간 'HH:MM'
 * @returns {number} - 근무시간 (분)
 */
export function calculateWorkMinutes(startTime, endTime) {
  const start = parseTimeToMinutes(startTime);
  const end = parseTimeToMinutes(endTime);
  return end - start;
}

/**
 * 근무시간을 시간:분 형식으로 변환
 * @param {number} minutes - 분 단위 시간
 * @returns {string} - 'X시간 Y분' 형식
 */
export function formatWorkDuration(minutes) {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  
  if (hours > 0 && mins > 0) {
    return `${hours}시간 ${mins}분`;
  } else if (hours > 0) {
    return `${hours}시간`;
  } else {
    return `${mins}분`;
  }
}

/**
 * 예상 급여 계산
 * @param {number} wageAmount - 시급
 * @param {string} startTime - 시작시간
 * @param {string} endTime - 종료시간
 * @returns {number} - 예상 급여
 */
export function calculateEstimatedWage(wageAmount, startTime, endTime) {
  const workMinutes = calculateWorkMinutes(startTime, endTime);
  const workHours = workMinutes / 60;
  return Math.round(wageAmount * workHours);
}

/**
 * 날짜 형식 변환 (YYYY-MM-DD)
 * @param {Date|string} date - Date 객체 또는 날짜 문자열
 * @returns {string} - 'YYYY-MM-DD' 형식 문자열
 */
export function formatDate(date) {
  const d = typeof date === 'string' ? new Date(date) : date;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * 현재 날짜 기준 앞뒤 날짜 범위 생성
 * @param {number} daysBefore - 이전 일수
 * @param {number} daysAfter - 이후 일수
 * @returns {object} - {startDate: string, endDate: string}
 */
export function getDateRange(daysBefore = 7, daysAfter = 30) {
  const today = new Date();
  const startDate = new Date(today);
  startDate.setDate(today.getDate() - daysBefore);
  
  const endDate = new Date(today);
  endDate.setDate(today.getDate() + daysAfter);
  
  return {
    startDate: formatDate(startDate),
    endDate: formatDate(endDate)
  };
}

/**
 * 날짜와 시간을 한글 형식으로 변환
 * @param {string} dateString - 'YYYY-MM-DD'
 * @param {string} startTime - 'HH:MM'
 * @param {string} endTime - 'HH:MM'
 * @returns {string} - '2026년 1월 15일 (월) 09:00-13:00'
 */
export function formatScheduleDateTime(dateString, startTime, endTime) {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const dayKorean = getDayNameKorean(getDayOfWeek(dateString));
  
  return `${year}년 ${month}월 ${day}일 (${dayKorean}) ${startTime}-${endTime}`;
}
